// src/components/SpeechIcon.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

import { subscribeToInboxMergedData } from "../services/messagesDataService";
import { subscribeToForumBadgeData } from "../services/forumDataService";
import { useBlockedUsers } from "../hooks/useBlockedUsers";

const SEEN_REPLIES_KEY = "forum_seen_replies_v1";

const readSeenRepliesMap = () => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_REPLIES_KEY) || "{}");
  } catch {
    return {};
  }
};

export default function SpeechIcon({
  className = "speech-icon",

  // Optional: externe, bereits gefilterte Werte
  unreadCount = null,
  myUnreadReplyCount = null,
  abosUnreadReplyCount = null,

  // Auf /forum/messages normalerweise Badge ausblenden,
  // mit forceShowBadge kann man es trotzdem anzeigen.
  forceShowBadge = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const myUid = user?.uid || null;
  const isVerified = user?.emailVerified === true;

  const { isBlocked, blockedUserIds } = useBlockedUsers(myUid, isVerified);

  const [internalUnread, setInternalUnread] = useState(0);
  const [seenRepliesMap, setSeenRepliesMap] = useState({});
  const [myReplyActivityMap, setMyReplyActivityMap] = useState({});
  const [abosReplyActivityMap, setAbosReplyActivityMap] = useState({});

  const hideBadge = useMemo(() => {
    if (forceShowBadge) return false;

    const p = location.pathname || "";
    return p.startsWith("/forum/messages") || p.startsWith("/messages");
  }, [location.pathname, forceShowBadge]);

  useEffect(() => {
    setSeenRepliesMap(readSeenRepliesMap());
  }, []);

  /**
   * Direktnachrichten-Badge laden.
   * Wichtig:
   * Nachrichten von blockierten Absendern werden NICHT gezählt.
   */
  useEffect(() => {
    let unsub = null;
    let mounted = true;

    const init = async () => {
      if (!myUid) {
        setInternalUnread(0);
        return;
      }

      try {
        unsub = await subscribeToInboxMergedData(myUid, (merged) => {
          if (!mounted) return;

          const list = Array.isArray(merged) ? merged : [];

          const count = list.reduce((acc, msg) => {
            const senderId = msg?.senderId || "";
            const isUnread =
              msg?.recipientId === myUid && msg?.read === false;

            if (!isUnread) return acc;

            // ✅ Blockierte Absender nicht zählen
            if (senderId && isBlocked(senderId)) {
              return acc;
            }

            return acc + 1;
          }, 0);

          setInternalUnread(count);
        });
      } catch (err) {
        console.warn(
          "SpeechIcon: subscribeToInboxMergedData fehlgeschlagen:",
          err
        );

        if (mounted) {
          setInternalUnread(0);
        }
      }
    };

    init();

    return () => {
      mounted = false;

      if (typeof unsub === "function") {
        unsub();
      }
    };
  }, [myUid, isBlocked, blockedUserIds]);

  /**
   * Forum-Badges laden.
   * Die eigentliche Blockierprüfung erfolgt unten bei countUnreadReplies().
   */
  useEffect(() => {
    let unsub = null;
    let mounted = true;

    const init = async () => {
      if (!myUid) {
        setMyReplyActivityMap({});
        setAbosReplyActivityMap({});
        return;
      }

      try {
        unsub = await subscribeToForumBadgeData(
          myUid,
          ({ myReplies, abosReplies }) => {
            if (!mounted) return;

            setMyReplyActivityMap(myReplies || {});
            setAbosReplyActivityMap(abosReplies || {});
          }
        );
      } catch (err) {
        console.warn(
          "SpeechIcon: subscribeToForumBadgeData fehlgeschlagen:",
          err
        );

        if (!mounted) return;

        setMyReplyActivityMap({});
        setAbosReplyActivityMap({});
      }
    };

    init();

    return () => {
      mounted = false;

      if (typeof unsub === "function") {
        unsub();
      }
    };
  }, [myUid]);

  /**
   * Ungelesene Forum-Antworten zählen.
   * Wichtig:
   * Antworten von blockierten Nutzern werden NICHT gezählt.
   */
  const countUnreadReplies = (activityMap) => {
    return Object.values(activityMap || {}).reduce((acc, meta) => {
      const postId = meta?.postId;
      const latestTs = Number(meta?.latestTs || 0);
      const lastSeen = Number(seenRepliesMap[postId] || 0);
      const latestAuthorId = meta?.latestAuthorId || "";

      if (!postId) return acc;
      if (!latestTs || latestTs <= lastSeen) return acc;
      if (!latestAuthorId || latestAuthorId === myUid) return acc;

      // ✅ Blockierte Antwort-Autoren nicht zählen
      if (isBlocked(latestAuthorId)) {
        return acc;
      }

      return acc + 1;
    }, 0);
  };

  const internalMyUnreadReplyCount = countUnreadReplies(myReplyActivityMap);
const internalAbosUnreadReplyCount = countUnreadReplies(abosReplyActivityMap);

const effectiveUnread =
  typeof unreadCount === "number"
    ? Math.min(unreadCount, internalUnread)
    : internalUnread;

const effectiveMyUnreadReplyCount =
  typeof myUnreadReplyCount === "number"
    ? Math.min(myUnreadReplyCount, internalMyUnreadReplyCount)
    : internalMyUnreadReplyCount;

const effectiveAbosUnreadReplyCount =
  typeof abosUnreadReplyCount === "number"
    ? Math.min(abosUnreadReplyCount, internalAbosUnreadReplyCount)
    : internalAbosUnreadReplyCount;

const hasUnread =
  !hideBadge &&
  (effectiveUnread > 0 ||
    effectiveMyUnreadReplyCount > 0 ||
    effectiveAbosUnreadReplyCount > 0);

  return (
    <div
      className={`speech-icon-wrapper ${className}`}
      onClick={() => navigate("/forum")}
      title="Nachrichten"
      style={{ cursor: "pointer" }}
    >
      <img
        src="/speech-icon.png"
        alt="Nachrichten"
        className="speech-icon-img"
      />

      {hasUnread && <span className="speech-badge" />}
    </div>
  );
}